import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { projects } from "../_data";
import { ScrollEffects } from "./effects";
import { ButtonMouseTracking } from "./button-effects";
import { EventBanner } from "./event-banner";
import { AnalyticsListener } from "../_analytics/analytics-listener";
import type { AnalyticsEventName } from "../_analytics/events";

/**
 * Vijf items, met de routevraag vooraan. Bouwbaarheid en Systemen staan hier
 * bewust niet meer: die zijn kindpagina's onder Werkwijze en worden vanuit
 * `/werkwijze` in de tekst aangewezen.
 */
const navigation = [
  ["Waar sta je?", "/projectrouter"],
  ["Projecten", "/projecten"],
  ["Pakketten en prijzen", "/manieren-van-werken"],
  ["Werkwijze", "/werkwijze"],
  ["Professionals", "/professionals"],
] as const;

export function SiteHeader() {
  return (
    <header className="site-header">
      <div className="shell site-header-inner">
        <Link className="brand-logo" href="/" aria-label="eVilla home">
          <Image src="/evilla-logo.png" alt="eVilla" width={166} height={77} priority />
        </Link>
        <nav className="main-nav" aria-label="Hoofdnavigatie">
          {navigation.map(([label, href]) => (
            <Link key={href} href={href}>{label}</Link>
          ))}
        </nav>
        <Link className="button button-primary button-small header-cta" href="/start">
          Leg je plan voor <span aria-hidden="true">→</span>
        </Link>
        <details className="mobile-nav">
          <summary aria-label="Open menu">
            <span aria-hidden="true">☰</span>
            <span className="sr-only">Menu</span>
          </summary>
          <nav aria-label="Mobiele navigatie">
            {([...navigation, ["Start", "/start"]] as [string, string][]).map(([label, href]) => (
              <Link key={href} href={href}>{label}<span aria-hidden="true">→</span></Link>
            ))}
          </nav>
        </details>
      </div>
    </header>
  );
}

export function SectionLabel({ children, light = false }: { children: ReactNode; light?: boolean }) {
  return <p className={`section-label${light ? " section-label-light" : ""}`}>{children}</p>;
}

export function ButtonLink({
  href, children, variant = "primary", size, event, eventData,
}: {
  href: string; children: ReactNode; variant?: "primary" | "dark" | "light" | "text"; size?: "small" | "large";
  /** Meten zonder clientboundary: zet een event en het gaat via de AnalyticsListener. */
  event?: AnalyticsEventName; eventData?: Record<string, string>;
}) {
  const cls = ["button", `button-${variant}`];
  if (size === "small") cls.push("button-small");
  if (size === "large") cls.push("button-large");
  return <Link className={cls.join(" ")} href={href} {...analyticsAttributes(event, eventData)}>{children}</Link>;
}

/** Zet een event en zijn metadata om in `data-analytics-*`-attributen. */
export function analyticsAttributes(event?: AnalyticsEventName, eventData?: Record<string, string>) {
  if (!event) return {};
  const attributes: Record<string, string> = { "data-analytics-event": event };
  for (const [field, value] of Object.entries(eventData ?? {})) {
    attributes[`data-analytics-${field}`] = value;
  }
  return attributes;
}

export function PageIntro({ label, title, text, children }: { label: string; title: string; text: string; children?: ReactNode }) {
  return (
    <section className="page-intro">
      <div className="shell page-intro-grid">
        <div>
          <SectionLabel>{label}</SectionLabel>
          <h1 className="display-l">{title}</h1>
        </div>
        <div className="page-intro-aside">
          <p>{text}</p>
          {children}
        </div>
      </div>
    </section>
  );
}

export function ProjectCard({ project }: { project: (typeof projects)[number] }) {
  return (
    <article className="project-card">
      <Link className="project-card-image" href={`/projecten/${project.slug}`}>
        <Image src={project.image} alt={project.imageAlt} fill sizes="(max-width: 760px) 100vw, 50vw" />
        <span className="image-label">{project.label}</span>
      </Link>
      <div className="project-card-body">
        <div className="project-meta">
          <span>{project.place}</span>
          {project.place !== project.status && <span>{project.status}</span>}
        </div>
        <h2><Link href={`/projecten/${project.slug}`}>{project.title}</Link></h2>
        <p>{project.intro}</p>
        <Link className="text-link" href={`/projecten/${project.slug}`}>Bekijk het verhaal <span aria-hidden="true">→</span></Link>
      </div>
    </article>
  );
}

export function RouteLink({ children, href = "/bouwbaarheid" }: { children: ReactNode; href?: string }) {
  return <Link className="text-link" href={href}>{children} <span aria-hidden="true">→</span></Link>;
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="shell footer-grid">
        <div>
          <Image className="footer-logo" src="/evilla-logo-wit.png" alt="eVilla" width={830} height={384} />
          <p className="footer-tag">Jouw tekening, onze fabriek.</p>
          <div className="footer-social">
            <a href="https://instagram.com/evilla.nl" target="_blank" rel="noopener noreferrer" aria-label="eVilla op Instagram">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1"/></svg>
            </a>
            <a href="https://linkedin.com/company/evillabenelux" target="_blank" rel="noopener noreferrer" aria-label="eVilla op LinkedIn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
            </a>
            <a href="https://maps.google.com/?q=eVilla+Peperstraat+9A+Naarden" target="_blank" rel="noopener noreferrer" aria-label="eVilla op Google Maps">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7z"/><circle cx="12" cy="9" r="3"/></svg>
            </a>
          </div>
        </div>
        <div className="footer-column">
          <span className="footer-heading">Verkennen</span>
          <Link href="/kavel">Ik heb een kavel</Link>
          <Link href="/ontwerp">Ik heb een ontwerp</Link>
          <Link href="/bouwrijp">Ik kan gaan bouwen</Link>
          <Link href="/projecten">Projecten</Link>
          <Link href="/bouwbaarheid">Bouwbaarheid</Link>
          <Link href="/werkwijze">Werkwijze</Link>
          <Link href="/systemen">Systemen</Link>
          <Link href="/manieren-van-werken">Pakketten en prijzen</Link>
        </div>
        <div className="footer-column">
          <span className="footer-heading">Starten</span>
          <Link href="/start">Leg je plan voor</Link>
          <a href="mailto:info@evilla.nl">info@evilla.nl</a>
          <Link href="/privacy">Wat we met je aanvraag doen</Link>
          <span>Nederland</span>
        </div>
      </div>
      <div className="shell footer-base">
        <span>© {new Date().getFullYear()} eVilla</span>
        <span>Bij elk beeld staat wat het is: eigen foto, ontwerpstudie of impressie.</span>
      </div>
    </footer>
  );
}

export function SiteShell({ children }: { children: ReactNode }) {
  return <><ScrollEffects /><ButtonMouseTracking /><AnalyticsListener /><EventBanner /><SiteHeader />{children}<SiteFooter /></>;
}